import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import { Badge } from 'primereact/badge';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationService } from '../../services/notificationService';
import { ActivityService } from '../../services/activityService';
import { Notification } from '../../types/notification';
import { Activity } from '../../types/activity';
import { getAgentInitials, getAgentDisplayName } from '../../utils/stringHelpers';
import './DashboardLayout.css';

export const DashboardLayout: React.FC = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const { currentAgent, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notificationPanel = useRef<OverlayPanel>(null);
  const activityPanel = useRef<OverlayPanel>(null);
  const profileMenu = useRef<Menu>(null);

  const menuItems = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      command: () => navigate('/agent/dashboard')
    },
    {
      label: 'Customers',
      icon: 'pi pi-users',
      items: [
        {
          label: 'View All',
          icon: 'pi pi-list',
          command: () => navigate('/agent/customers')
        },
        {
          label: 'Add Customer',
          icon: 'pi pi-plus',
          command: () => navigate('/agent/customers/add')
        }
      ]
    },
    {
      label: 'Insights',
      icon: 'pi pi-chart-line',
      command: () => navigate('/agent/insights')
    },
    {
      label: 'Notifications',
      icon: 'pi pi-bell',
      command: () => navigate('/agent/notifications')
    },
    {
      label: 'Recent Activity',
      icon: 'pi pi-history',
      command: () => navigate('/agent/activities')
    },
    {
      label: 'Subscription',
      icon: 'pi pi-credit-card',
      command: () => navigate('/agent/subscription')
    },
    {
      label: 'Gift Codes',
      icon: 'pi pi-gift',
      command: () => navigate('/agent/gifts')
    },
    {
      label: 'Profile',
      icon: 'pi pi-user',
      command: () => navigate('/agent/profile')
    },
    {
      label: 'Portal Guide',
      icon: 'pi pi-question-circle',
      command: () => navigate('/agent/guide')
    }
  ];

  // Add master agent and super agent menu items
  if (currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent') {
    const adminItems = [
      {
        label: 'Manage Agents',
        icon: 'pi pi-users',
        command: () => navigate('/agent/agents')
      },
      {
        label: 'Customer Assignments',
        icon: 'pi pi-sitemap',
        command: () => navigate('/agent/admin/assignments')
      }
    ];

    // Only super agents can access System Settings
    if (currentAgent?.role === 'super_agent') {
      adminItems.push(
        {
          label: 'System Settings',
          icon: 'pi pi-sliders-h',
          command: () => navigate('/agent/admin/settings')
        }
      );
    }

    menuItems.push({
      label: 'Administration',
      icon: 'pi pi-cog',
      items: adminItems
    });
  }

  const profileMenuItems = [
    {
      template: () => (
        <div className="profile-menu-header">
          <span className="profile-menu-name">{getAgentDisplayName(currentAgent)}</span>
          <span className="profile-menu-email">{currentAgent?.email}</span>
        </div>
      )
    },
    {
      separator: true
    },
    {
      label: 'Profile Settings',
      icon: 'pi pi-user',
      command: () => navigate('/agent/profile')
    },
    {
      separator: true
    },
    {
      label: 'Sign Out',
      icon: 'pi pi-sign-out',
      command: async () => {
        try {
          await signOut();
          navigate('/agent/login');
        } catch (error) {
          console.error('Error signing out:', error);
        }
      }
    }
  ];

  // Load notifications
  useEffect(() => {
    if (!currentAgent?.id) return;

    // Subscribe to real-time notifications
    const unsubscribe = NotificationService.subscribeToNotifications(
      currentAgent.id,
      (updatedNotifications) => {
        setNotifications(updatedNotifications.slice(0, 5)); // Show only recent 5
        const unread = updatedNotifications.filter(n => !n.read).length;
        setUnreadCount(unread);
      },
      { limit: 20 } // Fetch more to get accurate unread count
    );

    return () => unsubscribe();
  }, [currentAgent?.id]);

  // Load recent activities
  useEffect(() => {
    if (!currentAgent?.id) return;

    const loadActivities = async () => {
      const activities = await ActivityService.getRecentActivities(currentAgent.id, 5);
      setRecentActivities(activities);
    };

    loadActivities();
  }, [currentAgent?.id]);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/customers')) return 'Customers';
    if (path.includes('/insights')) return 'Insights';
    if (path.includes('/profile')) return 'Profile';
    if (path.includes('/notifications')) return 'Notifications';
    if (path.includes('/admin')) return 'Administration';
    return 'Agent Portal';
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      await NotificationService.markAsRead(notification.id);
    }
    
    // Hide panel
    notificationPanel.current?.hide();
    
    // Navigate if there's an action URL
    if (notification.metadata?.actionUrl) {
      navigate(notification.metadata.actionUrl);
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconMap: { [key: string]: string } = {
      'info': 'pi pi-info-circle',
      'success': 'pi pi-check-circle',
      'warning': 'pi pi-exclamation-triangle',
      'error': 'pi pi-times-circle',
      'agent_insight': 'pi pi-chart-line',
      'customer_update': 'pi pi-user',
      'system': 'pi pi-cog'
    };
    return iconMap[type] || 'pi pi-bell';
  };

  const formatNotificationTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const handleActivityClick = (activity: Activity) => {
    activityPanel.current?.hide();
    
    // Navigate based on entity type
    switch (activity.entityType) {
      case 'customer':
        navigate(`/agent/customers/${activity.entityId}`);
        break;
      case 'insight':
        navigate('/agent/insights');
        break;
      case 'notification':
        navigate('/agent/notifications');
        break;
      case 'agent':
        navigate('/agent/agents');
        break;
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="dashboard-sidebar-mobile"
      >
        <div className="sidebar-header">
          <img src="/ew-color-logo_crop.png" alt="EquityWorkx" className="sidebar-logo" />
          <h3>Agent Portal</h3>
        </div>
        <Menu model={menuItems} className="sidebar-menu" />
      </Sidebar>

      {/* Desktop Sidebar */}
      <div className="dashboard-sidebar-desktop">
        <div className="sidebar-header">
          <img src="/ew-color-logo_crop.png" alt="EquityWorkx" className="sidebar-logo" />
          <h3>Agent Portal</h3>
        </div>
        <Menu model={menuItems} className="sidebar-menu" />
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        {/* Top Navigation */}
        <div className="dashboard-topbar">
          <div className="topbar-left">
            <Button
              icon="pi pi-bars"
              className="topbar-menu-button"
              onClick={() => setSidebarVisible(true)}
            />
            <h2 className="page-title">{getPageTitle()}</h2>
          </div>
          
          <div className="topbar-right">
            {/* Icon buttons group */}
            <div className="topbar-icon-group">
              <div className="notification-button-wrapper">
                <button
                  className="topbar-icon-btn"
                  onClick={(e) => notificationPanel.current?.toggle(e)}
                  title="Notifications"
                >
                  <i className="pi pi-bell"></i>
                </button>
                {unreadCount > 0 && (
                  <span className="notification-count-badge">{unreadCount}</span>
                )}
              </div>

              <button
                className="topbar-icon-btn"
                onClick={(e) => activityPanel.current?.toggle(e)}
                title="Recent Activity"
              >
                <i className="pi pi-history"></i>
              </button>
            </div>

            {/* Divider */}
            <div className="topbar-divider"></div>

            {/* Profile section */}
            <div className="topbar-profile">
              <Menu
                model={profileMenuItems}
                popup
                ref={profileMenu}
                id="profile-menu"
                className="profile-dropdown"
              />
              <div
                className="profile-trigger"
                onClick={(e) => profileMenu.current?.toggle(e)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (e.key === ' ') e.preventDefault();
                    profileMenu.current?.toggle(e);
                  }
                }}
              >
                <Avatar
                  image={currentAgent?.profileImage}
                  label={getAgentInitials(currentAgent)}
                  size="normal"
                  shape="circle"
                  className="profile-avatar"
                />
                <div className="profile-info-compact">
                  <span className="profile-role-label">
                    {currentAgent?.role === 'super_agent' ? 'SUPER AGENT' :
                     currentAgent?.role === 'master_agent' ? 'MASTER AGENT' : 'AGENT'}
                  </span>
                </div>
                <i className="pi pi-chevron-down profile-chevron"></i>
              </div>
            </div>

            {/* Overlay Panels (rendered outside visual flow) */}
            <OverlayPanel
              ref={notificationPanel}
              className="notification-panel"
              style={{ width: '400px' }}
            >
              <div className="notification-panel-header">
                <h3>Notifications</h3>
                <Button
                  label="View All"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    notificationPanel.current?.hide();
                    navigate('/agent/notifications');
                  }}
                />
              </div>

              <ScrollPanel style={{ width: '100%', height: '300px' }}>
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <i className="pi pi-inbox" style={{ fontSize: '2rem', color: 'var(--text-color-secondary)' }}></i>
                    <p>No notifications</p>
                  </div>
                ) : (
                  <div className="notification-list">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`notification-item ${!notification.read ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            if (e.key === ' ') e.preventDefault();
                            handleNotificationClick(notification);
                          }
                        }}
                      >
                        <div className="notification-icon">
                          <i className={getNotificationIcon(notification.type)}></i>
                        </div>
                        <div className="notification-content">
                          <div className="notification-title">{notification.title}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-time">
                            {formatNotificationTime(notification.createdAt)}
                          </div>
                        </div>
                        {!notification.read && (
                          <Badge severity="info" className="notification-badge"></Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollPanel>
            </OverlayPanel>

            <OverlayPanel
              ref={activityPanel}
              className="notification-panel"
              style={{ width: '400px' }}
            >
              <div className="notification-panel-header">
                <h3>Recent Activity</h3>
                <Button
                  label="View All"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    activityPanel.current?.hide();
                    navigate('/agent/activities');
                  }}
                />
              </div>

              <ScrollPanel style={{ width: '100%', height: '300px' }}>
                {recentActivities.length === 0 ? (
                  <div className="notification-empty">
                    <i className="pi pi-history" style={{ fontSize: '2rem', color: 'var(--text-color-secondary)' }}></i>
                    <p>No recent activity</p>
                  </div>
                ) : (
                  <div className="notification-list">
                    {recentActivities.map((activity) => {
                      const { icon, color } = ActivityService.getActivityIcon(activity.activityType);
                      return (
                        <div
                          key={activity.id}
                          className="notification-item"
                          onClick={() => handleActivityClick(activity)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              if (e.key === ' ') e.preventDefault();
                              handleActivityClick(activity);
                            }
                          }}
                        >
                          <div className="notification-icon" style={{ backgroundColor: color, color: 'white' }}>
                            <i className={`pi ${icon}`}></i>
                          </div>
                          <div className="notification-content">
                            <div className="notification-title">
                              {activity.activityType.split('_').map(word =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ')}
                            </div>
                            <div className="notification-message">
                              {ActivityService.formatActivityDescription(activity)}
                            </div>
                            <div className="notification-time">
                              {formatNotificationTime(activity.timestamp)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollPanel>
            </OverlayPanel>
          </div>
        </div>

        {/* Page Content */}
        <div className="dashboard-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}; 